import { RecipeSearchBar } from "@/components/recipes/RecipeSearchBar";
import { AdvancedFilters } from "@/components/recipes/AdvancedFilters";
import { searchRecipes, type SearchFilters } from "@/lib/recipe-search-service";
import { addToSearchHistory } from "@/lib/search-history-service";
import { trackSearchPerformed, trackFilterApplied } from "@/lib/analytics-service";
import type { Recipe, RecipeBox } from "@/lib/types";
import { useEffect, useMemo, useState } from "react";

interface RecipeSearchProps {
  readonly recipes: Recipe[];
  readonly recipeBox: RecipeBox | null;
  readonly onResultsChange: (filteredRecipes: Recipe[]) => void;
}

export function RecipeSearch({ recipes, recipeBox, onResultsChange }: RecipeSearchProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<SearchFilters>({});

  const filteredRecipes = useMemo(() => {
    return searchRecipes(recipes, searchQuery, filters, {
      scope: "box",
      recipeBoxId: recipeBox?.id,
    });
  }, [recipes, searchQuery, filters, recipeBox?.id]);

  // Update parent component with filtered results
  useEffect(() => {
    onResultsChange(filteredRecipes);
  }, [filteredRecipes, onResultsChange]);

  // Save to search history and track analytics when search is performed
  useEffect(() => {
    if (searchQuery.trim()) {
      addToSearchHistory(searchQuery, filters as Record<string, unknown>);
      trackSearchPerformed(searchQuery, filteredRecipes.length, "box");
    }
    if (Object.keys(filters).length > 0) {
      const filterCount = Object.keys(filters).length;
      const filterTypes = Object.keys(filters).join(",");
      trackFilterApplied(filterTypes, filterCount);
    }
  }, [searchQuery, filters, filteredRecipes.length]);

  const handleFiltersChange = (newFilters: SearchFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({});
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex-1 w-full">
          <RecipeSearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search recipes by title, ingredients, tags..."
            showHistory={true}
          />
        </div>
        <div className="w-full sm:w-auto">
          <AdvancedFilters
            recipes={recipes}
            recipeBox={recipeBox}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onReset={handleResetFilters}
          />
        </div>
      </div>
      {searchQuery && (
        <p className="text-sm text-muted-foreground">
          Found {filteredRecipes.length} recipe{filteredRecipes.length !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}

